import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Upload, Wallet } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiError } from '@/lib/api-error';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { dayLabel, periodLabel, ratePercent, type BillingSummary } from '@/types/advisor-billing';
import { useProcessingFee } from '@/hooks/useProcessingFee';
import { money, moneyAvailable } from '@/types/offer';

const PAYOUT_TONE: Record<string, string> = {
  requested: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  paid: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  rejected: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

export default function AdvisorBillingPage() {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const { data: fee } = useProcessingFee();
  const { data, isLoading } = useQuery<BillingSummary>({
    queryKey: ['billing-summary', 'all'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/advisor/billing/summary/')).data;
    },
  });

  const balance = data?.balance ?? [];
  const chosen = currency || balance[0]?.currency || '';
  const available = balance.find((b) => b.currency === chosen)?.available ?? '0';

  const request = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const form = new FormData();
      form.append('amount', amount);
      form.append('currency', chosen);
      form.append('note', note);
      if (file) form.append('invoice', file);
      return api.post('/advisor/billing/payouts/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      toast.success('Payout requested. BitSwan will pay it against your invoice.');
      setAmount(''); setNote(''); setFile(null);
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    },
    onError: (e) => toast.error(apiError(e, 'Could not request the payout.')),
  });

  const download = async (id: string, filename: string) => {
    const api = await authenticatedBitswanBackendInstance();
    const res = await api.get(`/advisor/billing/payouts/${id}/invoice/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="advisor-billing-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Billing</h2>
        <p className="text-muted-foreground">
          What clients have paid through BitSwan for your packages and hours, and your payouts.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {balance.length === 0 && (
              <Card><CardContent className="pt-5 text-sm text-muted-foreground" data-testid="balance-empty">
                Nothing has been paid through BitSwan yet. Contracts billed through BitSwan show up here once a client pays their invoice.
              </CardContent></Card>
            )}
            {balance.map((b) => (
              <Card key={b.currency} data-testid={`balance-${b.currency}`}>
                <CardContent className="pt-5">
                  <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Available in {b.currency}</p>
                  <p className="mt-1 text-3xl font-bold tracking-tight" data-testid={`available-${b.currency}`}>{moneyAvailable(b.available, b.currency)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {money(b.earned, b.currency)} paid by clients
                    {parseFloat(b.fees) > 0 && (
                      <> · <span data-testid={`fee-${b.currency}`}>{money(b.fees, b.currency)} processing fee</span></>
                    )}
                    {' '}· {money(b.paid_out_or_requested, b.currency)} paid out or requested
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {fee && (
            <p className="text-xs text-muted-foreground" data-testid="processing-fee-line">
              {parseFloat(fee.rate_percent) > 0
                ? `BitSwan keeps ${ratePercent(fee.rate_percent)} of what clients pay through BitSwan. It comes out of your share, never out of what they pay.`
                : 'BitSwan keeps no processing fee at the moment.'}
              {fee.upcoming && (
                <> {` From ${dayLabel(fee.upcoming.effective_from)} it will be ${ratePercent(fee.upcoming.rate_percent)}.`}</>
              )}
              {' You are emailed whenever it changes.'}
            </p>
          )}

          {balance.length > 0 && (
            <Card data-testid="payout-form">
              <CardHeader className="pb-3"><CardTitle className="text-base">Request a payout</CardTitle>
                <p className="text-xs text-muted-foreground">Attach your invoice to BitSwan as a PDF; the payout is made against it.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-[160px_120px_1fr]">
                  <Input type="number" min="0" step="0.01" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payout-amount" />
                  <SearchableSelect
                    value={chosen}
                    onChange={setCurrency}
                    placeholder="Currency"
                    searchPlaceholder="Search currencies…"
                    options={balance.map((b) => ({ value: b.currency, label: b.currency }))}
                    testId="payout-currency"
                  />
                  <Input placeholder="Note for BitSwan (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
                    <Upload className="h-3.5 w-3.5" />
                    {file ? file.name : 'Attach your invoice (PDF)'}
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="payout-file" />
                  </label>
                  <Button onClick={() => request.mutate()} disabled={request.isPending || !file || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > parseFloat(available)} data-testid="payout-submit">
                    {request.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Request {amount && chosen ? money(amount, chosen) : 'payout'}
                  </Button>
                  <span className="text-xs text-muted-foreground">Up to {moneyAvailable(available, chosen || 'EUR')}.</span>
                </div>
              </CardContent>
            </Card>
          )}

          {(data?.payouts.length ?? 0) > 0 && (
            <Card data-testid="payout-list">
              <CardHeader className="pb-3"><CardTitle className="text-base">Payouts</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {data!.payouts.map((p) => (
                      <tr key={p.id} className="border-t" data-testid={`payout-${p.id}`}>
                        <td className="py-2">{new Date(p.requested_at).toLocaleDateString()}</td>
                        <td className="py-2 font-medium">{money(p.amount, p.currency)}</td>
                        <td className="py-2 text-muted-foreground">
                          <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => download(p.id, p.invoice_filename)}>
                            <Download className="h-3 w-3" /> {p.invoice_filename}
                          </button>
                        </td>
                        <td className="py-2 text-right">
                          <Badge className={PAYOUT_TONE[p.status]} data-testid={`payout-status-${p.id}`}>{p.status_display}</Badge>
                          {p.resolution_note && <p className="mt-1 text-xs text-muted-foreground">{p.resolution_note}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(data?.invoices.length ?? 0) > 0 && (
            <Card data-testid="advisor-invoice-list">
              <CardHeader className="pb-3"><CardTitle className="text-base">On clients' BitSwan invoices</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-1">Month</th><th className="py-1">Client</th><th className="py-1">Invoice</th><th className="py-1 text-right">Your share</th><th className="py-1 text-right">Fee</th><th className="py-1 text-right">Yours</th><th className="py-1 text-right">Status</th></tr></thead>
                  <tbody>
                    {data!.invoices.map((row) => (
                      <tr key={row.invoice_id} className="border-t" data-testid={`advisor-invoice-${row.invoice_number}`}>
                        <td className="py-2 font-medium">{periodLabel(row.billing_period)}</td>
                        <td className="py-2">{row.customer_name}</td>
                        <td className="py-2 text-muted-foreground">{row.invoice_number}</td>
                        <td className="py-2 text-right">{money(row.my_share, row.currency)}</td>
                        <td className="py-2 text-right text-muted-foreground">{money(row.processing_fee, row.currency)}</td>
                        <td className="py-2 text-right font-semibold">{money(row.my_net, row.currency)}</td>
                        <td className="py-2 text-right">
                          <Badge className={row.status === 'paid' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'}>
                            {row.status === 'paid' ? 'Paid' : row.status === 'overdue' ? 'Overdue' : 'Awaiting payment'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
